import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { ArrowRight, Brain, Newspaper, ShieldCheck, BarChart3, Zap, Layers } from "lucide-react";

const FEATURES = [
  { icon: Brain, title: "AI Trade Engine", body: "A proprietary multi-model AI consensus reads market structure on every chart and ranks setups by directional conviction.", testid: "feat-ai" },
  { icon: BarChart3, title: "TradingView Charts", body: "Institutional-grade charts embedded natively. Switch instantly across Indices, Equities, Crypto, FX & Commodities.", testid: "feat-tv" },
  { icon: Layers, title: "Multi-Timeframe Confluence", body: "Every signal is validated across 5m, 15m and 1H concurrently — no more lower-timeframe traps against the higher-timeframe trend.", testid: "feat-mtf" },
  { icon: Newspaper, title: "Real-Time News Impact", body: "Global newsflow filtered per-instrument. AI tags each headline as bullish, bearish, or neutral with confidence and reasoning.", testid: "feat-news" },
  { icon: ShieldCheck, title: "Risk-First Setups", body: "Defined invalidation level on every signal. Entry, stop loss, two targets, R:R and hold-time pre-calculated for intraday day-trading.", testid: "feat-risk" },
  { icon: Zap, title: "Probability-Scored", body: "Every setup carries a 52-92% probability score calibrated against multi-timeframe alignment and AI model agreement. Trade only the high-conviction calls.", testid: "feat-prob" },
];

const PLANS = [
  {
    id: "indian",
    label: "ProTradeIQ · Indian Markets",
    flag: "🇮🇳",
    monthly: 2999,
    annual: 29999,
    monthlyPlan: "indian_monthly",
    annualPlan: "indian_annual",
    featured: true,
    tagline: "For DHAN account holders — NIFTY, BANKNIFTY, SENSEX & NSE stocks.",
    features: [
      "AI trade signals on NIFTY, BANKNIFTY, SENSEX & NSE stocks",
      "Options Lab — Greeks, OI build-up, strike suggestions",
      "1-click execution + Auto-Trade engine on YOUR own DHAN account",
      "Verified track record with full trade audit",
      "Probability + R:R + hold-time on every setup",
    ],
    testid: "plan-indian",
  },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white overflow-x-hidden">
      <Navbar variant="marketing" />

      <section className="pt-40 pb-24 px-6 md:px-12 relative">
        <div
          className="absolute inset-0 opacity-30 pointer-events-none"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?crop=entropy&cs=srgb&fm=jpg&q=85')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            maskImage: "linear-gradient(to bottom, black 0%, transparent 70%)",
            WebkitMaskImage: "linear-gradient(to bottom, black 0%, transparent 70%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto">
          <div className="text-xs font-mono tracking-[0.3em] text-[#007AFF] mb-6 uppercase" data-testid="hero-tagline">
            ◢ AI day-trading copilot
          </div>
          <h1 className="font-heading text-5xl md:text-7xl lg:text-8xl tracking-tighter font-black uppercase leading-[0.95] max-w-5xl" data-testid="hero-headline">
            Smarter trades.<br />
            <span className="text-[#007AFF]">Higher</span> conviction.
          </h1>
          <p className="mt-8 max-w-2xl text-lg md:text-xl text-zinc-400 leading-relaxed" data-testid="hero-subhead">
            ProTradeIQ is an AI-powered intraday trading platform. A multi-model AI consensus engine reads charts across timeframes, scores every setup with a probability, and tracks global news impact in real time — so you trade only the calls that survive the filter.
          </p>
          <div className="mt-12 flex flex-wrap gap-4">
            <Link to="/signup" className="bg-[#007AFF] text-white font-bold uppercase tracking-wider py-4 px-8 hover:bg-[#3395FF] transition-colors flex items-center gap-2 group" data-testid="hero-cta-primary">
              Start trading <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <a href="#features" className="border border-zinc-700 hover:border-zinc-500 bg-transparent text-white font-bold uppercase tracking-wider py-4 px-8 transition-colors" data-testid="hero-cta-secondary">
              See how it works
            </a>
          </div>
          <div className="mt-20 grid grid-cols-3 gap-8 max-w-2xl">
            {[
              { k: "3×", v: "AI Model Consensus" },
              { k: "3 TF", v: "5m · 15m · 1H Aligned" },
              { k: "<200ms", v: "Signal Latency" },
            ].map((s) => (
              <div key={s.v} className="border-l border-zinc-800 pl-4">
                <div className="font-mono text-3xl md:text-4xl font-black">{s.k}</div>
                <div className="text-xs uppercase tracking-widest text-zinc-500 mt-1">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="features" className="py-24 px-6 md:px-12 border-t border-zinc-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-xs font-mono tracking-[0.3em] text-[#007AFF] mb-4 uppercase">◢ The stack</div>
          <h2 className="font-heading text-4xl md:text-5xl tracking-tight font-bold uppercase max-w-3xl">
            Built like a quant desk.<br />Priced like software.
          </h2>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-900">
            {FEATURES.map((f) => (
              <div key={f.title} className="bg-[#0A0A0A] p-8 hover:bg-[#121212] transition-colors group" data-testid={f.testid}>
                <f.icon className="h-8 w-8 text-[#007AFF] mb-6" strokeWidth={1.5} />
                <h3 className="font-heading text-xl font-bold uppercase tracking-tight">{f.title}</h3>
                <p className="mt-3 text-zinc-400 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="pricing" className="py-24 px-6 md:px-12 border-t border-zinc-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-xs font-mono tracking-[0.3em] text-[#007AFF] mb-4 uppercase">◢ Pricing</div>
          <h2 className="font-heading text-4xl md:text-5xl tracking-tight font-bold uppercase">One platform. Your DHAN account.</h2>
          <p className="mt-4 text-zinc-400 max-w-xl">Introductory offer for DHAN account holders. Start with a 3-day free trial — cancel anytime before charge.</p>

          <div className="mt-16 grid grid-cols-1 gap-6 max-w-xl mx-auto">
            {PLANS.map((p) => (
              <div
                key={p.id}
                className={`relative p-10 border ${p.featured ? "border-[#007AFF] bg-[#007AFF]/5" : "border-[#F59E0B]/40 bg-[#121212]"}`}
                data-testid={p.testid}
              >
                {p.featured && (
                  <div className="absolute -top-3 left-10 bg-[#007AFF] text-xs font-bold uppercase tracking-widest px-3 py-1">
                    Most popular
                  </div>
                )}
                <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">{p.flag} {p.label}</div>
                <div className="mt-4 flex items-baseline gap-2">
                  <span className="font-heading font-black text-6xl tracking-tighter">₹{p.monthly.toLocaleString("en-IN")}</span>
                  <span className="text-zinc-500 text-sm uppercase tracking-wider">/month</span>
                </div>
                <div className="mt-1 text-sm text-zinc-400" data-testid={`${p.testid}-annual-price`}>
                  or ₹{p.annual.toLocaleString("en-IN")}/year <span className="text-[#22C55E]">(save ₹{(p.monthly * 12 - p.annual).toLocaleString("en-IN")})</span>
                </div>
                <div className="mt-2 text-sm text-zinc-400">{p.tagline}</div>
                <ul className="mt-8 space-y-3 text-sm text-zinc-300">
                  {p.features.map((b) => (
                    <li key={b} className="flex gap-3"><span className="text-[#22C55E] font-mono">▸</span>{b}</li>
                  ))}
                </ul>
                <Link
                  to="/signup"
                  className={`mt-10 block text-center font-bold uppercase tracking-wider py-4 transition-colors ${
                    p.featured ? "bg-[#007AFF] text-white hover:bg-[#3395FF]" : "border border-zinc-700 text-white hover:border-zinc-500"
                  }`}
                  data-testid={`${p.testid}-cta`}
                >
                  Start 3-day free trial
                </Link>
                <div className="mt-3 text-[10px] uppercase tracking-widest text-zinc-500 text-center font-mono">No charge for 3 days · Cancel anytime</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t border-zinc-900 py-12 px-6 md:px-12">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between gap-6 text-xs uppercase tracking-widest text-zinc-600">
          <div>© 2026 ProTradeIQ · Educational use only · Not financial advice</div>
          <div className="flex gap-6">
            <a href="#features" className="hover:text-white">Features</a>
            <a href="#pricing" className="hover:text-white">Pricing</a>
            <Link to="/login" className="hover:text-white">Sign in</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
